import { orgolasRepo } from '@/repo/OrgolasRepo';
import { PatientSessionInfo } from '@informme/shared/schemas/bunduril/patient-ui/patient-scans-qr-code.schema';
import { FormStatus } from '@informme/shared/schemas/form/FormStage2Schema';
import { GetPartialOrgResponse } from '@informme/shared/schemas/orgolas/get-partial-org.schema';
import { Module, VuexModule } from 'vuex-class-modules';
import store from '../store';

const STORAGE_KEYS = {
  patientSessionInfo: 'patientSessionInfo',
  orgId: 'orgId',
  processId: 'processId',
  currentFormId: 'currentFormId',
  currentQrId: 'currentQrId',
  currentPageIndex: 'currentPageIndex',
  filledFormIds: 'filledFormIds',
  formSelectionPageAlreadyVisited: 'formSelectionPageAlreadyVisited',
  isClosing: 'isClosing',
} as const;

/** Stores the patient session info that was used to create the QR Code that was scanned.
 * (The QR-ID was used to initiate the session and download the patientSessionInfo)
 * The patientSessionInfo has the attribute `patientSessionId` which is used to
 * authenticate the user in future requests.
 *
 * Runtime reads/writes use this in-memory store. sessionStorage is hydrated on load
 * and synced after mutations so page refresh can restore state.
 */
@Module({ generateMutationSetters: true })
class SessionStore extends VuexModule {
  /** patientSessionInfo that was used to create the personalized link */
  patientSessionInfo: PatientSessionInfo | null = null;

  /** processId of the currently active/open FFP */
  processId: string | null = null;

  orgId: string | null = null;

  org: GetPartialOrgResponse | null = null;

  /** formId tied to the active processId (set together in patient-opens-form) */
  currentFormId: string | null = null;

  currentQrId: string | null = null;

  /** Page index within the active form; used only for refresh recovery */
  currentPageIndex: number | null = null;

  formSelectionPageAlreadyVisited = false;

  /** Distinguishes tab close from refresh in FormViewer */
  isClosing = false;

  get assignedFormIds(): string[] {
    return this.patientSessionInfo?.assignedFormIds ?? [];
  }

  get formStatuses(): FormStatus[] {
    return this.patientSessionInfo?.formStatuses ?? [];
  }

  get filledFormIds(): string[] {
    const completed = new Set<string>();
    for (const status of this.formStatuses) {
      if (status.completedAtISO) {
        completed.add(status.formId);
      }
    }
    return [...completed];
  }

  isFormCompleted(formId: string): boolean {
    return this.formStatuses.some((status) => status.formId === formId && status.completedAtISO);
  }

  areAllFormsCompleted(): boolean {
    if (this.assignedFormIds.length === 0) {
      return false;
    }
    return this.assignedFormIds.every((formId) => this.isFormCompleted(formId));
  }

  hasMoreFormsToFill(): boolean {
    return (
      this.assignedFormIds.length > 0 && this.assignedFormIds.length !== this.filledFormIds.length
    );
  }

  async loadOrg(orgId: string): Promise<GetPartialOrgResponse> {
    const sessionId = this.patientSessionInfo?.patientSessionId;
    if (!sessionId) {
      throw new Error('Cannot load org: no patient session available');
    }
    this.org = await orgolasRepo.getOrg(orgId, sessionId);
    return this.org;
  }

  /** Hydrate in-memory state from sessionStorage (e.g. on page load / refresh). */
  loadFromSessionStorage(): boolean {
    const storedOrgId = sessionStorage.getItem(STORAGE_KEYS.orgId);
    const storedPatientSessionInfo = sessionStorage.getItem(STORAGE_KEYS.patientSessionInfo);
    const storedProcessId = sessionStorage.getItem(STORAGE_KEYS.processId);
    const storedCurrentFormId = sessionStorage.getItem(STORAGE_KEYS.currentFormId);
    const storedQrId = sessionStorage.getItem(STORAGE_KEYS.currentQrId);
    const storedPageIndex = sessionStorage.getItem(STORAGE_KEYS.currentPageIndex);
    const storedVisited = sessionStorage.getItem(STORAGE_KEYS.formSelectionPageAlreadyVisited);

    if (storedOrgId) {
      this.orgId = storedOrgId;
    }

    if (storedPatientSessionInfo) {
      try {
        this.patientSessionInfo = JSON.parse(storedPatientSessionInfo) as PatientSessionInfo;
      } catch {
        this.patientSessionInfo = null;
        sessionStorage.removeItem(STORAGE_KEYS.patientSessionInfo);
      }
    }

    // Restore processId only when currentFormId is also present — both are written together
    // in patient-opens-form and cleared together on form completion.
    if (storedProcessId && storedCurrentFormId) {
      this.processId = storedProcessId;
      this.currentFormId = storedCurrentFormId;
    } else {
      this.processId = null;
      this.currentFormId = null;
    }

    this.currentQrId = storedQrId;
    const pageIndex = storedPageIndex ? Number.parseInt(storedPageIndex, 10) : NaN;
    this.currentPageIndex = Number.isFinite(pageIndex) ? pageIndex : null;
    this.formSelectionPageAlreadyVisited = storedVisited === 'true';

    return !!(this.orgId && this.patientSessionInfo);
  }

  /** Persist current in-memory session fields to sessionStorage. */
  syncToSessionStorage(): void {
    if (this.patientSessionInfo) {
      sessionStorage.setItem(
        STORAGE_KEYS.patientSessionInfo,
        JSON.stringify(this.patientSessionInfo),
      );
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.patientSessionInfo);
    }

    if (this.orgId) {
      sessionStorage.setItem(STORAGE_KEYS.orgId, this.orgId);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.orgId);
    }

    if (this.processId && this.currentFormId) {
      sessionStorage.setItem(STORAGE_KEYS.processId, this.processId);
      sessionStorage.setItem(STORAGE_KEYS.currentFormId, this.currentFormId);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.processId);
      sessionStorage.removeItem(STORAGE_KEYS.currentFormId);
    }

    if (this.currentQrId) {
      sessionStorage.setItem(STORAGE_KEYS.currentQrId, this.currentQrId);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.currentQrId);
    }

    if (this.currentPageIndex !== null && !Number.isNaN(this.currentPageIndex)) {
      sessionStorage.setItem(STORAGE_KEYS.currentPageIndex, String(this.currentPageIndex));
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.currentPageIndex);
    }

    sessionStorage.setItem(STORAGE_KEYS.filledFormIds, JSON.stringify(this.filledFormIds));

    if (this.formSelectionPageAlreadyVisited) {
      sessionStorage.setItem(STORAGE_KEYS.formSelectionPageAlreadyVisited, 'true');
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.formSelectionPageAlreadyVisited);
    }
  }

  setPatientSessionInfo(info: PatientSessionInfo | null): void {
    this.patientSessionInfo = info;
    this.syncToSessionStorage();
  }

  setOrgId(orgId: string | null): void {
    this.orgId = orgId;
    this.syncToSessionStorage();
  }

  setCurrentQrId(qrId: string | null): void {
    this.currentQrId = qrId;
    this.syncToSessionStorage();
  }

  setFormSelectionPageAlreadyVisited(visited: boolean): void {
    this.formSelectionPageAlreadyVisited = visited;
    this.syncToSessionStorage();
  }

  setCurrentPageIndex(index: number | null): void {
    this.currentPageIndex = index;
    this.syncToSessionStorage();
  }

  clearCurrentPageIndex(): void {
    this.currentPageIndex = null;
    sessionStorage.removeItem(STORAGE_KEYS.currentPageIndex);
  }

  setIsClosing(closing: boolean): void {
    this.isClosing = closing;
  }

  /** Set active FFP process; optionally tie it to a form. Pass null to clear. */
  setActiveFormProcess(processId: string | null, formId: string | null = null): void {
    this.processId = processId;
    this.currentFormId = formId;
    this.syncToSessionStorage();
  }

  /** Record form completion in patientSessionInfo and sync to sessionStorage. */
  markFormCompleted(formId: string, processId: string | null): void {
    if (!this.patientSessionInfo) {
      return;
    }

    const patientSessionInfo: PatientSessionInfo = {
      ...this.patientSessionInfo,
      formStatuses: [...(this.patientSessionInfo.formStatuses ?? [])],
    };
    const { formStatuses } = patientSessionInfo;

    const existingStatusIndex = formStatuses.findIndex((status) => status.formId === formId);

    if (existingStatusIndex !== -1) {
      formStatuses[existingStatusIndex] = {
        ...formStatuses[existingStatusIndex],
        completedAtISO: new Date().toISOString(),
        processId: processId ?? formStatuses[existingStatusIndex].processId,
      };
    } else {
      formStatuses.push({
        formId,
        processId: processId ?? undefined,
        openedAtISO: new Date().toISOString(),
        completedAtISO: new Date().toISOString(),
      });
    }

    this.patientSessionInfo = patientSessionInfo;
    this.syncToSessionStorage();
  }

  /** Clear active form FFP state (processId, currentFormId, page index). */
  clearActiveFormProcess(): void {
    this.processId = null;
    this.currentFormId = null;
    this.currentPageIndex = null;
    sessionStorage.removeItem(STORAGE_KEYS.processId);
    sessionStorage.removeItem(STORAGE_KEYS.currentFormId);
    sessionStorage.removeItem(STORAGE_KEYS.currentPageIndex);
  }

  /** Clear all session state from memory and sessionStorage. */
  clearSession(): void {
    this.patientSessionInfo = null;
    this.processId = null;
    this.orgId = null;
    this.org = null;
    this.currentFormId = null;
    this.currentQrId = null;
    this.currentPageIndex = null;
    this.formSelectionPageAlreadyVisited = false;
    this.isClosing = false;
    sessionStorage.clear();
  }

  getStoredPatientSessionId(): string | null {
    const stored = sessionStorage.getItem(STORAGE_KEYS.patientSessionInfo);
    if (!stored) {
      return null;
    }
    try {
      const parsed = JSON.parse(stored) as PatientSessionInfo;
      return parsed.patientSessionId ?? null;
    } catch {
      return null;
    }
  }

  hasStoredSessionData(): boolean {
    return !!(
      sessionStorage.getItem(STORAGE_KEYS.orgId) ||
      sessionStorage.getItem(STORAGE_KEYS.patientSessionInfo)
    );
  }
}

export const sessionStore = new SessionStore({ store, name: 'session' });
